import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AlertTriangle } from 'lucide-react';

import { atualizarArea, criarArea, fetchModelosPorFinalidade, type Area, type AreaInput } from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { DocumentosRequeridosEditor } from './DocumentosRequeridosEditor';
import { clausulaGravada, HonorariosCampos, temPreco, useHonorariosForm } from './HonorariosCampos';
import { QuestionarioEditor } from './QuestionarioEditor';
import { useAdvogadoEmEdicao } from './advogadoEmEdicao';

interface AreaFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Área a editar; ``null`` cria uma nova. */
  area: Area | null;
  /** Chamado ao CRIAR uma área (mantém o Dialog aberto p/ adicionar docs/perguntas). */
  onCreated?: (area: Area) => void;
  /** Os honorários gerais do advogado — valem para a área sem honorários próprios. */
  honorariosGerais?: Record<string, unknown> | null;
}

/** Ordem das abas — a mesma do "Salvar e seguir". */
const ABAS = ['detalhes', 'honorarios', 'documentos', 'perguntas'] as const;
type Aba = (typeof ABAS)[number];

const SEM_MODELO = '__none__';

export function AreaForm({ open, onOpenChange, area, onCreated, honorariosGerais }: AreaFormProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const editando = area !== null;

  const [nome, setNome] = useState(area?.nome ?? '');
  const [ativo, setAtivo] = useState(area?.ativo ?? true);
  const [contratoId, setContratoId] = useState(area?.template_contrato_id ?? SEM_MODELO);
  const [procuracaoId, setProcuracaoId] = useState(area?.template_procuracao_id ?? SEM_MODELO);
  const honorarios = useHonorariosForm(area?.honorarios);
  // "Outros assuntos" não herda os honorários gerais (backend:
  // `honorarios_efetivos`): é o que não se encaixa em área nenhuma, e o advogado
  // avalia caso a caso. Oferecer o switch ali mostraria um preço que não vale.
  const herdaGerais = area?.catalogo_chave !== 'outros';
  // Área sem honorários próprios usa os gerais — é o comum, e o que poupa o
  // advogado de repetir o mesmo preço em cada área. Sem gerais ainda, os campos
  // já vêm abertos: ligar "usar os gerais" ali não daria preço a ninguém. Área
  // com ALGUM honorário gravado (mesmo sem preço: só condições, `moeda`) abre nos
  // campos dela — com os gerais ligados, salvar a área por qualquer aba gravaria
  // `null` e apagaria esses dados sem que o advogado os visse.
  const semHonorariosProprios = Object.keys(area?.honorarios ?? {}).length === 0;
  const [usarGerais, setUsarGerais] = useState(
    () => herdaGerais && semHonorariosProprios && temPreco(honorariosGerais),
  );
  const geraisTemPreco = herdaGerais && temPreco(honorariosGerais);
  const clausulaGeral = clausulaGravada(honorariosGerais);
  // Esta área diz quanto custa? Com os gerais, depende deles.
  const comPreco = usarGerais ? geraisTemPreco : honorarios.temPreco;

  // Os modelos que valem para a área são os do advogado dela (§4.4).
  const advogado = useAdvogadoEmEdicao();
  const donoDaArea = area?.advogado_responsavel_id ?? advogado?.id ?? null;
  const [aba, setAba] = useState<Aba>('detalhes');
  const indiceAba = ABAS.indexOf(aba);
  const ultimaAba = indiceAba === ABAS.length - 1;
  const seguir = () => setAba(ABAS[Math.min(indiceAba + 1, ABAS.length - 1)]);
  const voltarAba = () => setAba(ABAS[Math.max(indiceAba - 1, 0)]);

  const { data: modelosContrato, error: erroContrato } = useQuery({
    queryKey: queryKeys.secretario.modelos('CONTRATO_HONORARIOS', donoDaArea),
    queryFn: () =>
      donoDaArea
        ? fetchModelosPorFinalidade('CONTRATO_HONORARIOS', donoDaArea)
        : fetchModelosPorFinalidade('CONTRATO_HONORARIOS'),
    enabled: open,
  });
  const { data: modelosProcuracao, error: erroProcuracao } = useQuery({
    queryKey: queryKeys.secretario.modelos('PROCURACAO', donoDaArea),
    queryFn: () =>
      donoDaArea ? fetchModelosPorFinalidade('PROCURACAO', donoDaArea) : fetchModelosPorFinalidade('PROCURACAO'),
    enabled: open,
  });
  // Erro na busca não pode ser silencioso (o select fica vazio sem explicação).
  const erroModelos = erroContrato ?? erroProcuracao;

  const mutation = useMutation({
    mutationFn: (payload: AreaInput) =>
      editando
        ? atualizarArea(area.id, payload)
        : criarArea(advogado ? { ...payload, advogado_responsavel_id: advogado.id } : payload),
    // Salvar não fecha: o "Salvar e seguir" leva à próxima aba (e é ele que
    // decide para onde ir). Ao criar, o pai passa a tratar o form como edição
    // da área nova — sem remontá-lo, para não perder a aba nem o que foi digitado.
    onSuccess: (salva) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess(editando ? 'Área salva.' : 'Área criada.');
      if (!editando) onCreated?.(salva);
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const salvar = (depois?: () => void) => {
    if (!nome.trim()) {
      setAba('detalhes');
      return;
    }
    mutation.mutate(
      {
        nome: nome.trim(),
        ativo,
        // Com os gerais, a área fica sem honorários próprios (é o que faz os gerais valerem).
        honorarios: usarGerais ? null : honorarios.paraSalvar(),
        template_contrato_id: contratoId === SEM_MODELO ? null : contratoId,
        template_procuracao_id: procuracaoId === SEM_MODELO ? null : procuracaoId,
      },
      { onSuccess: () => depois?.() },
    );
  };

  // Detalhes e Honorários são o que o PUT da área grava; Documentos e Perguntas
  // salvam cada item ao adicioná-lo — ali o botão só avança.
  const abaComSalvar = aba === 'detalhes' || aba === 'honorarios';
  const primario = () => {
    if (abaComSalvar) salvar(ultimaAba ? () => onOpenChange(false) : seguir);
    else if (ultimaAba) onOpenChange(false);
    else seguir();
  };
  const rotuloPrimario = abaComSalvar ? 'Salvar e seguir' : ultimaAba ? 'Concluir' : 'Seguir';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    primario();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Rolagem só no corpo: o rodapé (Voltar / Salvar e seguir) fica sempre à vista. */}
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>{editando ? `Editar área: ${area.nome}` : 'Nova área de atuação'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'Ajuste os detalhes, documentos e perguntas desta área.'
              : 'Dê um nome à área e siga: honorários, documentos e perguntas vêm em seguida.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={aba} onValueChange={(v) => setAba(v as Aba)} className="flex min-h-0 flex-1 flex-col">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
            <TabsTrigger value="honorarios">
              Honorários
              {/* O ponto some quando há preço. É o único requisito desta tela que
                  o advogado não descobre sozinho: sem ele a área não chega ao
                  contrato e faz só o pré-atendimento. */}
              {!comPreco && (
                <>
                  <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                  <span className="sr-only">(pendente)</span>
                </>
              )}
            </TabsTrigger>
            <TabsTrigger value="documentos" disabled={!editando}>
              Documentos
            </TabsTrigger>
            <TabsTrigger value="perguntas" disabled={!editando}>
              Perguntas
            </TabsTrigger>
          </TabsList>

          <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
            <TabsContent value="detalhes">
              <form onSubmit={handleSubmit} className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="areaNome">Nome da área</Label>
                  <Input
                    id="areaNome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Ex.: Trabalhista"
                    maxLength={80}
                    required
                  />
                </div>

                <p className="text-xs text-muted-foreground">
                  Os modelos abaixo são <strong>opcionais</strong>: em branco, a área usa os modelos padrão da
                  configuração. Defina só se quiser um contrato/procuração específico para esta área.
                </p>

                {erroModelos != null && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Não foi possível carregar os modelos: {classifyError(erroModelos).message}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="areaContrato">Modelo de contrato de honorários (opcional)</Label>
                  <Select value={contratoId} onValueChange={setContratoId}>
                    <SelectTrigger id="areaContrato">
                      <SelectValue placeholder="Padrão da configuração" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SEM_MODELO}>Usar o padrão</SelectItem>
                      {(modelosContrato ?? []).map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.titulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="areaProcuracao">Modelo de procuração (opcional)</Label>
                  <Select value={procuracaoId} onValueChange={setProcuracaoId}>
                    <SelectTrigger id="areaProcuracao">
                      <SelectValue placeholder="Padrão da configuração" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SEM_MODELO}>Usar o padrão</SelectItem>
                      {(modelosProcuracao ?? []).map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.titulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label htmlFor="areaAtivo">Área ativa</Label>
                  <Switch id="areaAtivo" checked={ativo} onCheckedChange={setAtivo} />
                </div>
              </form>
            </TabsContent>

            <TabsContent value="honorarios">
              <div className="space-y-4 py-2">
                <p className="text-xs text-muted-foreground">
                  É o que entra no contrato que o cliente assina. O Secretário informa exatamente o que
                  estiver aqui — nunca negocia, nunca arredonda e nunca inventa valor.
                </p>

                {herdaGerais ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                    <Label htmlFor="areaUsarGerais" className="leading-snug">
                      Usar meus honorários gerais
                    </Label>
                    <Switch id="areaUsarGerais" checked={usarGerais} onCheckedChange={setUsarGerais} />
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Esta área não usa os seus honorários gerais: casos de outros assuntos você avalia um a um.
                    Sem um preço próprio aqui, ela faz só o pré-atendimento.
                  </p>
                )}

                {!comPreco && (
                  <Alert variant="warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {usarGerais && herdaGerais
                        ? 'Você ainda não informou honorários gerais. '
                        : 'Informe o valor fixo, o percentual de êxito ou os dois. '}
                      Sem preço, esta área faz só o pré-atendimento: o Secretário ouve o caso, recolhe os
                      documentos e passa o atendimento para você, sem contrato.
                    </AlertDescription>
                  </Alert>
                )}

                {usarGerais ? (
                  clausulaGeral && (
                    <div className="space-y-1 rounded-lg border bg-muted/40 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Como fica no contrato</p>
                      <p className="text-sm">{clausulaGeral}</p>
                    </div>
                  )
                ) : (
                  <HonorariosCampos form={honorarios} idPrefixo="area-hon" />
                )}
              </div>
            </TabsContent>

            <TabsContent value="documentos" className="py-2">
              {editando && (
                <DocumentosRequeridosEditor
                  escopo={{ tipo: 'area', areaId: area.id }}
                  documentos={area.documentos_requeridos}
                />
              )}
            </TabsContent>

            <TabsContent value="perguntas" className="py-2">
              {editando && (
                <QuestionarioEditor escopo={{ tipo: 'area', areaId: area.id }} perguntas={area.perguntas} />
              )}
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="gap-2 border-t pt-4 sm:justify-between">
          <Button type="button" variant="ghost" onClick={voltarAba} disabled={indiceAba === 0}>
            Voltar
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <Button type="button" onClick={primario} disabled={mutation.isPending || !nome.trim()}>
              {mutation.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
              {rotuloPrimario}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
